import React from 'react'
import { Link } from 'react-router-dom'

const PrivacyPolicyPage = () => {
  return (
    <main className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Header */}
        <header className="mb-10 sm:mb-14">
          <p className="text-sm font-semibold text-emerald-600 mb-2 uppercase tracking-wide">
            Privacy Policy
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-light text-slate-900 mb-4 leading-tight">
            How Obule protects and uses your information
          </h1>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-3xl font-light">
            At Obule, we are committed to protecting your privacy and being transparent about how we
            collect, use, and share your information. This Privacy Policy explains what data we
            collect, why we collect it, how we use it, and the choices you have about your
            information when you use our websites, mobile experiences, dashboards, and related
            services (collectively, the &quot;Services&quot;).
          </p>
          <p className="mt-3 text-sm text-slate-500">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </header>

        <div className="space-y-10">
          {/* 1. Information we collect */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              1. Information we collect
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              We collect information to provide and improve the Services, to keep your account
              secure, to enable event organizers and centers to run their events and offers
              effectively, and to comply with legal requirements. The information we collect falls
              into three broad categories: information you provide directly, information we collect
              automatically, and information from third parties.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base text-slate-600 font-light">
              <li>
                <span className="font-semibold text-slate-800">Account and profile data.</span>{' '}
                When you create an attendee or center account, we collect information such as your
                name, email address, password, country, city, phone number, organization details,
                payout details, and other information you choose to provide in your profile.
              </li>
              <li>
                <span className="font-semibold text-slate-800">Transaction and order data.</span>{' '}
                When you purchase a ticket or accept an offer, we collect information about the
                order such as the events or offers you purchase, ticket types, quantities, prices,
                discounts, payment status, and timestamps. We do not store full card numbers or CVV
                codes; those are handled by our payment provider.
              </li>
              <li>
                <span className="font-semibold text-slate-800">
                  Communications and support interactions.
                </span>{' '}
                If you contact us or interact with our support channels, we collect the content of
                your messages, attachments, and any additional information you choose to provide so
                that we can respond to your requests and improve our Services.
              </li>
              <li>
                <span className="font-semibold text-slate-800">
                  Device, usage, and log information.
                </span>{' '}
                When you use Obule, we automatically collect certain technical information such as
                your IP address, browser type, operating system, device identifiers, pages you
                visit, referring/exit pages, timestamps, and other usage data. We use this
                information to secure the platform, diagnose technical issues, analyze trends, and
                understand how the Services are used.
              </li>
              <li>
                <span className="font-semibold text-slate-800">
                  Cookies and similar technologies.
                </span>{' '}
                We use cookies, local storage, and similar technologies to remember your
                preferences, keep you signed in, measure performance, and personalize content. You
                can control cookies through your browser settings, but some features of the
                Services may not function properly without them.
              </li>
              <li>
                <span className="font-semibold text-slate-800">Third‑party data.</span> We may
                receive information about you from event organizers, payment providers, analytics
                vendors, or other partners where this is permitted by law and aligned with this
                Policy—for example, fraud signals from our payment gateway, or aggregated analytics
                about how campaigns perform.
              </li>
            </ul>
          </section>

          {/* 2. How we use your information */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              2. How we use your information
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              We use the information we collect for a variety of business and operational purposes.
              We process your information only where we have a valid legal basis to do so, such as
              your consent, our legitimate interests in operating and improving the Services, the
              performance of a contract with you, and compliance with legal obligations.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base text-slate-600 font-light">
              <li>To create and maintain your account and user profile.</li>
              <li>To process your orders, issue tickets, and manage your bookings and offers.</li>
              <li>
                To allow centers and event organizers to manage their events and offers, view
                analytics, and fulfill their obligations to attendees.
              </li>
              <li>
                To communicate with you about your account, orders, security alerts, updates,
                changes to our terms, and other important notices.
              </li>
              <li>
                To personalize your experience, such as highlighting relevant offers or surfacing
                events that match your interests or location.
              </li>
              <li>
                To monitor, detect, and prevent fraud, abuse, security incidents, and unauthorized
                access to the Services.
              </li>
              <li>
                To analyze usage trends and improve the design, performance, and functionality of
                the Obule platform.
              </li>
              <li>
                To comply with applicable laws, regulations, legal processes, and law enforcement
                requests; and to enforce our Terms of Use and other policies.
              </li>
            </ul>
          </section>

          {/* 3. Cookies and similar technologies */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              3. Cookies and similar technologies
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              Cookies are small text files stored on your device that help us recognize you and
              remember your preferences. We use first‑party and third‑party cookies, as well as
              local storage, pixels, and similar technologies for several purposes:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base text-slate-600 font-light mb-3">
              <li>Keeping you signed in across pages and sessions.</li>
              <li>Remembering your language, country, and display preferences.</li>
              <li>Measuring how many people visit our pages and how they interact with them.</li>
              <li>Helping us detect suspicious or fraudulent activity.</li>
              <li>
                Showing you content and offers that are more relevant to your interests and
                location.
              </li>
            </ul>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              You can control cookies through your browser settings and other tools. However, if you
              disable certain cookies, some features of the Services may not work as intended. By
              continuing to use Obule after seeing our cookie banner and choosing &quot;Accept&quot;,
              you consent to our use of cookies as described in this Policy.
            </p>
          </section>

          {/* 4. Payments and security */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">4. Payments and security</h2>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              Obule works with trusted payment partners to process card and bank transactions
              securely. When you make a payment, certain information is transmitted directly to our
              payment provider and is handled under their own security and privacy practices. We do
              not store full payment card numbers, CVV codes, or sensitive authentication data on
              our servers.
            </p>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              We implement technical and organizational safeguards designed to protect your personal
              data against unauthorized access, loss, alteration, or destruction. These measures
              include encryption in transit, access controls, audit logging, role‑based permissions,
              and regular monitoring. However, no online service can guarantee absolute security,
              and you share responsibility by keeping your password confidential and promptly
              notifying us of any suspected unauthorized activity.
            </p>
          </section>

          {/* 5. How we share information */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              5. How we share your information
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              We do not sell your personal information. We share information only in the following
              limited circumstances:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base text-slate-600 font-light">
              <li>
                <span className="font-semibold text-slate-800">With centers and organizers.</span>{' '}
                When you purchase a ticket or redeem an offer, we share necessary information (such
                as your name, contact details, and order details) with the relevant center or event
                organizer so that they can manage access and fulfill their obligations.
              </li>
              <li>
                <span className="font-semibold text-slate-800">With service providers.</span> We work
                with third‑party vendors who help us operate the Services, such as hosting
                providers, payment processors, analytics vendors, email delivery platforms, and
                customer support tools. These providers are bound by contractual obligations to
                protect your information and use it only for the purposes we specify.
              </li>
              <li>
                <span className="font-semibold text-slate-800">For legal reasons.</span> We may
                disclose information if we believe it is reasonably necessary to comply with a law,
                regulation, legal process, or governmental request; to enforce our Terms of Use or
                other agreements; to protect the rights, property, or safety of Obule, our users, or
                the public; or to detect and prevent fraud or security issues.
              </li>
              <li>
                <span className="font-semibold text-slate-800">Business transfers.</span> If we are
                involved in a merger, acquisition, restructuring, or sale of assets, your
                information may be transferred as part of that transaction. In such cases, we will
                take steps to ensure your privacy rights continue to be respected.
              </li>
            </ul>
          </section>

          {/* 6. Data retention */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">6. Data retention</h2>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              We retain your personal information for as long as necessary to provide the Services,
              comply with our legal obligations, resolve disputes, enforce our agreements, and
              support legitimate business operations. The specific retention period depends on the
              nature of the data and the context in which it was collected. When we no longer need
              your information, we will delete or anonymize it in line with our data retention
              practices and applicable laws.
            </p>
          </section>

          {/* 7. Your rights and choices */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              7. Your rights and choices
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              Depending on your location and applicable law, you may have certain rights regarding
              your personal information, such as the right to access, correct, update, or delete
              your data; to object to or restrict certain processing; or to withdraw consent where
              you have given it. You can exercise many of these rights directly through your Obule
              account settings, or by contacting us using the details below.
            </p>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              We will respond to reasonable requests within a reasonable timeframe and may need to
              verify your identity before fulfilling your request. In some circumstances, we may be
              unable to fully comply with a request, for example where doing so would conflict with
              legal obligations or internal record‑keeping requirements, but we will explain our
              reasoning whenever we are required to do so.
            </p>
          </section>

          {/* 8. International data transfers */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              8. International data transfers
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              Obule may process and store information on servers located in different countries
              than the one in which you reside. Where we transfer personal data across borders, we
              take steps to ensure that appropriate safeguards are in place, such as using
              contractual protections, standard contractual clauses, or relying on other lawful
              transfer mechanisms. By using the Services, you acknowledge that your information may
              be processed in countries that may have different data protection laws than your home
              jurisdiction.
            </p>
          </section>

          {/* 9. Children */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              9. Children&apos;s privacy
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              Obule is not directed to children under the age of 16, and we do not knowingly
              collect personal information from children under 16. If we become aware that a child
              under 16 has provided us with personal data, we will take steps to delete such
              information and, where appropriate, close the associated account. If you believe that
              a child has provided us with personal information, please contact us using the details
              below.
            </p>
          </section>

          {/* 10. Changes and contact */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              10. Changes to this Privacy Policy
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              We may update this Privacy Policy from time to time to reflect changes in our
              practices, technologies, legal requirements, or other factors. When we make material
              changes, we will notify you through the Services, by email, or by other reasonable
              means and update the &quot;Last updated&quot; date at the top of this page. We
              encourage you to review this Policy periodically to stay informed about how we handle
              your information.
            </p>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              If you have questions about this Privacy Policy or how we handle your personal
              information, you can contact us via the{' '}
              <Link to="/contact" className="text-emerald-600 hover:text-emerald-700 underline">
                Contact Us
              </Link>{' '}
              page or by using any contact details provided within your Obule dashboard.
            </p>
          </section>
        </div>
      </div>
    </main>
  )
}

export default PrivacyPolicyPage

